import type { ThreadEscalationInfo } from "@redotech/merchant-sdk/rpc/schema/agents/merchant-support-agent/message-merchant-support-agent";
import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { useLoad } from "@redotech/react-util/load";
import BoxIcon from "@redotech/redo-icons/box-01.svg";
import { GlobalAgentContext } from "@redotech/redo-merchant-app-common/global-agent-context";
import { RedoMerchantRpcClientContext } from "@redotech/redo-merchant-app-common/rpc-client";
import { UserContext } from "@redotech/redo-merchant-app-common/user";
import { AssistantMessageContent } from "@redotech/redo-model/agents/llm-messages";
import { AiSlideOutChatGlobal } from "@redotech/redo-web/ai/chat/components/slide-out-chat-modal-global";
import { AiChatContext } from "@redotech/redo-web/ai/chat/contexts/ai-chat-context";
import { useAiSlidePanel } from "@redotech/redo-web/ai/chat/contexts/ai-slide-panel-context";
import {
  MessageAuthorContext,
  MessageAuthorContextType,
  MessageAuthorInfo,
} from "@redotech/redo-web/ai/chat/contexts/message-author-context";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { Tooltip } from "@redotech/redo-web/tooltip/tooltip";
import { ObjectId } from "bson";
import {
  memo,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useIntercom } from "react-use-intercom";
import { useEscalationContext } from "./escalation-context";
import { EscalationStatus } from "./escalation-status";
import { useInChatEscalationFlag } from "./in-chat-escalation-flag-provider";
import {
  clearEscalationNotification,
  useEscalationNotifications,
} from "./use-escalation-notifications";
import {
  sanitizeIntercomBody,
  useHumanMessageHandler,
} from "./use-human-message-handler";

type SupportThread = {
  _id: string;
  updatedAt: string | Date;
  messages: { id: string; role: string; content: string | unknown[] }[];
  escalation?: ThreadEscalationInfo;
};

export type HelpChatGlobalProps = {
  onChatOpen?: () => void;
  onLoadThread?: (threadId: string, thread: SupportThread) => void;
  onRegisterTriageThreadCallback?: (
    callback: (threadId: string) => void,
  ) => void | (() => void);
};

export const HelpChatGlobal = memo(function HelpChatGlobal({
  onChatOpen,
  onLoadThread,
  onRegisterTriageThreadCallback,
}: HelpChatGlobalProps) {
  const inChatEscalation = useInChatEscalationFlag();
  const [currentThreadId, setCurrentThreadId] = useState<string | undefined>();
  const [threadsRefreshKey, setThreadsRefreshKey] = useState(0);
  const hasInitializedRef = useRef(false);
  const processedMessageIdsRef = useRef<Set<string>>(new Set());
  const authorRegistryRef = useRef<Map<string, MessageAuthorInfo>>(new Map());
  const pendingThreadLoadInFlightRef = useRef(false);
  const isOpenRef = useRef(false);

  const messageAuthorContextValue = useMemo<MessageAuthorContextType>(
    () => ({
      getAuthor: (messageId: string) =>
        authorRegistryRef.current.get(messageId),
    }),
    [],
  );
  const user = useRequiredContext(UserContext);
  const intercom = useIntercom();
  const merchantRpcClient = useRequiredContext(RedoMerchantRpcClientContext);
  const { close, open, isOpen, panelOptions, clearPanelOptions } =
    useAiSlidePanel();
  const { pageContext, defaultSuggestedQuestions } =
    useContext(GlobalAgentContext);

  // Keep isOpenRef in sync
  useEffect(() => {
    isOpenRef.current = isOpen;
  }, [isOpen]);

  // Always call hook (can't be conditional), but only use when feature is enabled
  const escalationContext = useEscalationContext();
  const clearEscalation = useMemo(
    () => (inChatEscalation ? escalationContext.clearEscalation : () => {}),
    [escalationContext.clearEscalation, inChatEscalation],
  );
  const { setEscalation, setTriageThreadId } = escalationContext;

  const suggestedQuestions =
    pageContext?.suggestedQuestions ?? defaultSuggestedQuestions;

  const refreshThreads = useCallback(() => {
    setThreadsRefreshKey((prev) => prev + 1);
  }, []);

  const handleNewTriageThread = useCallback(
    (threadId: string) => {
      setCurrentThreadId(threadId);
      refreshThreads();
    },
    [refreshThreads],
  );

  useEffect(() => {
    if (onRegisterTriageThreadCallback) {
      const cleanup = onRegisterTriageThreadCallback(handleNewTriageThread);
      if (typeof cleanup === "function") {
        return cleanup;
      }
      return undefined;
    }
    return undefined;
  }, [onRegisterTriageThreadCallback, handleNewTriageThread]);

  const threadsLoad = useLoad(async () => {
    if (!user.email) {
      return { threads: [] };
    }
    return await merchantRpcClient.getMerchantSupportAgentThreads({
      userEmail: user.email,
    });
  }, [merchantRpcClient, user.email, threadsRefreshKey]);

  const {
    clearMessages,
    clearAssets,
    setInputValue,
    stopStream,
    addMessage,
    sendMessage,
  } = useContext(AiChatContext);

  const threads = useMemo(
    () => threadsLoad.value?.threads || [],
    [threadsLoad.value?.threads],
  );

  const loadThreadIntoChat = useHandler(
    async (threadId: string, thread: SupportThread) => {
      // Check if current escalation context matches this thread
      const contextEscalation =
        inChatEscalation &&
        escalationContext.triageThreadId === threadId &&
        escalationContext.escalation.escalationId &&
        (escalationContext.escalation.status === "waiting" ||
          escalationContext.escalation.status === "human-active")
          ? {
              escalationId: escalationContext.escalation.escalationId,
              status: escalationContext.escalation.status as
                | "waiting"
                | "human-active",
              noticeTopic: escalationContext.escalation.noticeTopic || "",
              humanJoinedAt: null,
              closedAt: null,
            }
          : undefined;
      const threadEscalation = thread.escalation ?? contextEscalation;

      const hasEscalation = inChatEscalation && !!threadEscalation;
      if (hasEscalation) {
        setIsLoadingThread(true);
      }

      try {
        stopStream();
        clearAssets();
        clearMessages();
        setInputValue("");
        if (inChatEscalation) {
          clearEscalation();
          setAgentName(null);
          setAgentAvatarUrl(null);
          processedMessageIdsRef.current.clear();
          authorRegistryRef.current.clear();
        }

        // Add AI messages from the thread
        for (const [index, message] of thread.messages.entries()) {
          if (message.role === "tool") continue;

          const rawContent: any = (message as any).content;
          const contentToUse =
            typeof rawContent === "string" || Array.isArray(rawContent)
              ? rawContent
              : String(rawContent ?? "");

          if (typeof contentToUse === "string" && contentToUse.trim() === "") {
            continue;
          }

          addMessage({
            id: message.id ?? new ObjectId().toString() + index,
            role: message.role,
            content: contentToUse as any,
          } as any);
        }

        // Fetch and display escalation messages if present
        if (inChatEscalation && threadEscalation) {
          const escalation = threadEscalation;
          const isActiveEscalation =
            escalation.status === "waiting" ||
            escalation.status === "human-active";

          const messagesResult = await merchantRpcClient.getEscalationMessages({
            escalationId: escalation.escalationId,
          });

          // Combine and sort all messages by createdAt
          type CombinedMessage =
            | {
                type: "human";
                message: (typeof messagesResult.messages)[number];
              }
            | {
                type: "user";
                message: (typeof messagesResult.userMessages)[number];
              };

          const allMessages: CombinedMessage[] = [
            ...messagesResult.messages.map((m) => ({
              type: "human" as const,
              message: m,
            })),
            ...messagesResult.userMessages.map((m) => ({
              type: "user" as const,
              message: m,
            })),
          ].sort((a, b) => a.message.createdAt - b.message.createdAt);

          for (const { type, message } of allMessages) {
            processedMessageIdsRef.current.add(message.id);

            if (type === "human") {
              const humanMessage =
                message as (typeof messagesResult.messages)[number];

              if (humanMessage.authorName) {
                setAgentName(humanMessage.authorName);
              }
              if (humanMessage.authorAvatarUrl) {
                setAgentAvatarUrl(humanMessage.authorAvatarUrl);
              }

              let content: string | AssistantMessageContent[] =
                sanitizeIntercomBody(humanMessage.body);

              if (
                humanMessage.attachments &&
                humanMessage.attachments.length > 0
              ) {
                const contentParts: AssistantMessageContent[] = [];
                const cleanedBody = sanitizeIntercomBody(humanMessage.body);
                if (cleanedBody) {
                  contentParts.push({
                    type: "text" as const,
                    text: cleanedBody,
                  });
                }

                for (const attachment of humanMessage.attachments) {
                  const isImage = attachment.contentType?.startsWith("image/");
                  const isVideo = attachment.contentType?.startsWith("video/");
                  const attachmentData = JSON.stringify({
                    url: attachment.url,
                    type: attachment.contentType,
                    name:
                      isImage || isVideo
                        ? attachment.name
                        : attachment.name || "Attachment",
                  });
                  const marker = isImage
                    ? "image-data"
                    : isVideo
                      ? "video-data"
                      : "document-data";
                  contentParts.push({
                    type: "text" as const,
                    text: `[[[[${marker}:${attachmentData}]]]]`,
                  });
                }
                content = contentParts;
              }

              const messageId = `human-${humanMessage.id}`;
              authorRegistryRef.current.set(messageId, {
                authorName: humanMessage.authorName ?? null,
                authorAvatarUrl: humanMessage.authorAvatarUrl ?? null,
              });

              addMessage({ id: messageId, role: "assistant", content });
            } else {
              const userMessage =
                message as (typeof messagesResult.userMessages)[number];
              addMessage({
                id: `escalation-user-${userMessage.id}`,
                role: "user",
                content: sanitizeIntercomBody(userMessage.body),
              } as any);
            }
          }

          // Set up escalation context for active escalations only
          if (isActiveEscalation) {
            setTriageThreadId(threadId);
            setEscalation({
              escalationId: escalation.escalationId,
              noticeTopic: escalation.noticeTopic,
              status: escalation.status,
              intercomConversationId: null,
            });
          }
        }

        onLoadThread?.(threadId, thread);
        setCurrentThreadId(threadId);
      } catch (err) {
        console.error("[loadThreadIntoChat] Error loading thread:", err);
      } finally {
        setIsLoadingThread(false);
      }
    },
  );

  const {
    isLoadingThread,
    setIsLoadingThread,
    notifyClosedEscalationMessage,
    setDismissedEscalationId,
  } = useEscalationNotifications({
    inChatEscalation,
    isOpen,
    open,
    threads,
    escalationContext,
    merchantRpcClient,
    loadThreadIntoChat,
    userEmail: user.email,
    hasInitializedRef,
    pendingThreadLoadInFlightRef,
  });

  const { agentName, setAgentName, agentAvatarUrl, setAgentAvatarUrl } =
    useHumanMessageHandler({
      addMessage,
      processedMessageIdsRef,
      authorRegistryRef,
      isOpenRef,
      onClosedPanelMessage: notifyClosedEscalationMessage,
    });

  const startNewConversation = useHandler(() => {
    onChatOpen?.();
    stopStream();
    clearAssets();
    clearMessages();
    setInputValue("");
    setCurrentThreadId(undefined);
    if (inChatEscalation) {
      clearEscalation();
      clearEscalationNotification();
      setDismissedEscalationId(undefined);
      setAgentName(null);
      setAgentAvatarUrl(null);
      processedMessageIdsRef.current.clear();
      authorRegistryRef.current.clear();
    }
    hasInitializedRef.current = true;
  });

  // When the panel opens, always start a new conversation
  useEffect(() => {
    if (isOpen && !hasInitializedRef.current) {
      startNewConversation();
    }

    // Reset initialization flag when panel closes
    if (!isOpen) {
      hasInitializedRef.current = false;
    }
  }, [isOpen, startNewConversation]);

  // Handle prefill input when panel opens with options
  useEffect(() => {
    if (panelOptions?.prefillInput) {
      setInputValue(panelOptions.prefillInput);
      if (panelOptions.autoSubmit) {
        const prefillValue = panelOptions.prefillInput;
        const timeoutId = setTimeout(() => {
          sendMessage(prefillValue);
        }, 100);
        return () => clearTimeout(timeoutId);
      }
      clearPanelOptions();
    }
    return undefined;
  }, [panelOptions, setInputValue, sendMessage, clearPanelOptions]);

  // Legacy: Open Intercom messages (only when feature is disabled)
  const openIntercomAction = useCallback(() => {
    close();
    intercom.showMessages();
  }, [close, intercom]);

  const messageHistoryButton = useMemo(
    () => (
      <Tooltip arrow placement="top" title="Message history with team">
        <RedoButton
          hierarchy="secondary"
          IconLeading={BoxIcon}
          onClick={openIntercomAction}
        />
      </Tooltip>
    ),
    [openIntercomAction],
  );

  // Memoize the escalation status element to keep a stable reference during resize
  const escalationStatusElement = useMemo(
    () => (
      <EscalationStatus agentAvatarUrl={agentAvatarUrl} agentName={agentName} />
    ),
    [agentAvatarUrl, agentName],
  );

  // New escalation UI: show escalation status above input
  if (inChatEscalation) {
    return (
      <MessageAuthorContext.Provider value={messageAuthorContextValue}>
        <AiSlideOutChatGlobal
          aboveInput={escalationStatusElement}
          currentThreadId={currentThreadId}
          hideClearButton
          isLoadingThread={isLoadingThread}
          onClose={close || (() => {})}
          onLoadThread={loadThreadIntoChat}
          onNewConversation={startNewConversation}
          suggestedQuestions={suggestedQuestions}
          threads={threads}
        />
      </MessageAuthorContext.Provider>
    );
  }

  // Legacy UI: show Intercom message history button
  return (
    <AiSlideOutChatGlobal
      currentThreadId={currentThreadId}
      hideClearButton
      isLoadingThread={isLoadingThread}
      leadingToolbarContent={messageHistoryButton}
      onClose={close || (() => {})}
      onLoadThread={loadThreadIntoChat}
      onNewConversation={startNewConversation}
      suggestedQuestions={suggestedQuestions}
      threads={threads}
    />
  );
});
